<template>
    <section>
        <FileTableHeaderControls
            v-model:search="search"
            v-model:visibleFields="visibleFields"
            :columns="availableColumns"
            :isExportDisabled="!files.length"
            :visibleColumns
            @exportClipboard="exportClipboard"
            @exportCsv="exportCsv"
        />

        <ErrorState
            v-if="error"
            @refetch="searchFiles"
        />

        <FileTableNoFilesForUser
            v-else-if="!isLoading && totalRecordsUnfiltered === 0"
            @createFile="scrollToFileDetailsSection"
        />

        <FileTableEmptySearchResult
            v-else-if="!isLoading && files.length === 0 && search"
            @clearSearch="search = ''"
        />

        <DsDataTable
            v-else
            v-model:page="page"
            v-model:rows="rows"
            ref="dataTable"
            clickableRows
            exportFilename="Pre-close Files"
            :loading="isLoading"
            :sortField
            :sortOrder
            :totalRecords
            :value="displayFiles"
            @rowClick="handleRowClick"
            @sort="handleSort"
        >
            <DsColumn
                v-if="isVisible('ref_number')"
                field="ref_number"
                header="File reference #"
                :sortable="isSortable('ref_number')"
            >
                <template #body="{ data }: FileRowSlot">{{ data.ref_number || EMPTY_CELL_CONTENT }}</template>
            </DsColumn>

            <DsColumn
                v-if="isVisible('address')"
                field="address"
                header="Property address"
                :sortable="isSortable('address')"
            >
                <template #body="{ data }: FileRowSlot">
                    <div class="whitespace-normal break-words">{{ data.address || EMPTY_CELL_CONTENT }}</div>
                </template>
            </DsColumn>

            <DsColumn
                v-if="isVisible('clients_count')"
                class="text-center"
                field="clients_count"
                header="Clients"
                :sortable="isSortable('clients_count')"
            >
                <template #body="{ data }: FileRowSlot">{{ data.clients_count }}</template>
            </DsColumn>

            <DsColumn
                v-if="isVisible('balance_due_formatted')"
                field="balance_due_formatted"
                header="Balance due"
                headerClass="money-cell"
                :sortable="isSortable('balance_due_formatted')"
                sortField="balance_due"
            >
                <template #body="{ data }: FileRowSlot">
                    <div class="flex justify-end">
                        {{ data.balance_due_formatted || EMPTY_CELL_CONTENT }}
                    </div>
                </template>
            </DsColumn>

            <DsColumn
                v-if="isVisible('close_date')"
                field="close_date"
                header="Close date"
                :sortable="isSortable('close_date')"
            >
                <template #body="{ data }: FileRowSlot">{{ data.close_date || EMPTY_CELL_CONTENT }}</template>
            </DsColumn>

            <DsColumn
                v-if="isVisible('company')"
                field="company"
                header="Company"
                :sortable="isSortable('company')"
            >
                <template #body="{ data }: FileRowSlot">{{ data.company || EMPTY_CELL_CONTENT }}</template>
            </DsColumn>

            <DsColumn
                v-if="isVisible('branch')"
                field="branch"
                header="Branch"
                :sortable="isSortable('branch')"
            >
                <template #body="{ data }: FileRowSlot">{{ data.branch || EMPTY_CELL_CONTENT }}</template>
            </DsColumn>

            <DsColumn
                v-if="isVisible('vendor')"
                field="vendor"
                header="Source"
                :sortable="isSortable('vendor')"
            >
                <template #body="{ data }: FileRowSlot">{{ data.vendor || EMPTY_CELL_CONTENT }}</template>
            </DsColumn>

            <DsColumn
                v-if="isVisible('created_at')"
                class="text-center"
                field="created_at"
                header="Created"
                :sortable="isSortable('created_at')"
            >
                <template #body="{ data }: FileRowSlot">{{ data.created_at || EMPTY_CELL_CONTENT }}</template>
            </DsColumn>

            <DsColumn
                v-if="isVisible('updated_at')"
                class="text-center"
                field="updated_at"
                header="Updated"
                :sortable="isSortable('updated_at')"
            >
                <template #body="{ data }: FileRowSlot">{{ data.updated_at || EMPTY_CELL_CONTENT }}</template>
            </DsColumn>

            <DsColumn
                v-if="isVisible('owned_by_email')"
                field="owned_by_email"
                header="Owned by"
                :sortable="isSortable('owned_by_email')"
            >
                <template #body="{ data }: FileRowSlot">{{ data.owned_by_email || EMPTY_CELL_CONTENT }}</template>
            </DsColumn>

            <DsColumn
                class="w-20 text-right"
                clickDisabled
                header="Actions"
            >
                <template #body="{ data }: FileRowSlot">
                    <FileTableActions
                        :file="data"
                        @click.stop
                        @deleteFile="openDeleteFile"
                        @editFile="openEditFile"
                        @showEventLog="openEventLog"
                        @showStreetView="openStreetView"
                    />
                </template>
            </DsColumn>
        </DsDataTable>

        <StreetViewDialog
            v-if="hasStreetView(selectedFile)"
            v-model:open="isStreetViewOpen"
            :address="selectedFile.address"
            :streetViewUrl="selectedFile.street_view_url"
        />

        <EventLog
            v-if="selectedFile"
            v-model:open="isEventLogOpen"
            :key="`event-log-${selectedFile.id}`"
            :route="selectedFile.routes.file_history"
        />

        <DeleteFileDialog
            v-if="selectedFile"
            v-model:file="selectedFile"
            v-model:open="isDeleteFileOpen"
            @deleted="handleFileDeleted"
        />

        <EditFile
            v-if="selectedFile"
            v-model:open="isEditFileOpen"
            :key="`edit-file-${selectedFile.id}`"
            :file="selectedFile"
            @saved="handleFileSaved"
        />
    </section>
</template>

<script setup lang="ts">
import { useAddFileFormScroller } from '@/company-portal/add-file/use-add-file-form-scroller';
import { EMPTY_CELL_CONTENT } from '@/components/Components/static-content';
import useDsToast from '@/components/design-system/toast/useDsToast';
import ErrorState from '@/components/shared/ErrorState.vue';
import DeleteFileDialog from '@/components/Views/Company/File/DeleteFileDialog.vue';
import EditFile from '@/components/Views/Company/File/EditFile.vue';
import EventLog from '@/components/Views/Company/File/EventLog.vue';
import { hasStreetView, toFileTableDisplayRow } from '@/components/Views/Company/File/file-table-utils';
import type { FileRowSlot, FileTableDisplayRow } from '@/components/Views/Company/File/file-table.types';
import FileTableActions from '@/components/Views/Company/File/FileTableActions.vue';
import FileTableEmptySearchResult from '@/components/Views/Company/File/FileTableEmptySearchResult.vue';
import FileTableHeaderControls from '@/components/Views/Company/File/FileTableHeaderControls.vue';
import FileTableNoFilesForUser from '@/components/Views/Company/File/FileTableNoFilesForUser.vue';
import StreetViewDialog from '@/components/Views/Company/File/StreetViewDialog.vue';
import { useFileTable } from '@/components/Views/Company/File/use-file-table';
import { useFileTableColumns } from '@/components/Views/Company/File/use-file-table-columns';
import { useFileTableExport } from '@/components/Views/Company/File/use-file-table-export';
import { safeRedirect } from '@/shared/utils/safe-redirect';
import { useUserStore } from '@/stores/company/user.store';
import { DsColumn } from '@ds/table/DsColumn';
import DsDataTable from '@ds/table/DsDataTable.vue';
import { computed, nextTick, ref, useTemplateRef } from 'vue';

const userStore = useUserStore();

const { addToast } = useDsToast();
const { scrollToFileDetailsSection } = useAddFileFormScroller();

const {
    page,
    rows,
    search,
    sortField,
    sortDirection,
    files,
    totalRecords,
    totalRecordsUnfiltered,
    error,
    isFetching,
    searchFiles,
    handleSort,
} = useFileTable();

const sortOrder = computed(() => (sortDirection.value === 'asc' ? 1 : -1));

const isLoading = computed(() => isFetching.value || userStore.isFetchingMeDetail);

const { availableColumns, visibleColumns, visibleFields, isVisible, isSortable } = useFileTableColumns({
    balance_due_formatted: () => userStore.showBalanceDue,
    close_date: () => userStore.showCloseDate,
});

const displayFiles = computed<FileTableDisplayRow[]>(() => files.value.map(toFileTableDisplayRow));

const dataTable = useTemplateRef('dataTable');

const { exportCsv, exportClipboard } = useFileTableExport({
    dataTable,
    columns: visibleColumns,
    rows: displayFiles,
});

const selectedFile = ref<FileTableDisplayRow>();

const isDeleteFileOpen = ref(false);
const isEditFileOpen = ref(false);
const isStreetViewOpen = ref(false);
const isEventLogOpen = ref(false);

// Ensures that at least one tick is elapsed between selectedFile being set and the dialog
// model being changed. This connects with the usage of v-if in the sheet/dialog wrappers
// so the change has a chance to fire their internal watchers.
// potential Ds-level refinement to address this in CLENG-3998
const withSelectedFile = (action: () => void) => (file: FileTableDisplayRow) => {
    selectedFile.value = file;
    nextTick(action);
};

// EditFile takes no id: it needs the full details payload, which the list rows do not carry
const openEditFile = withSelectedFile(() => {
    isEditFileOpen.value = true;
});

const openDeleteFile = withSelectedFile(() => {
    isDeleteFileOpen.value = true;
});

const openStreetView = withSelectedFile(() => {
    isStreetViewOpen.value = true;
});

const openEventLog = withSelectedFile(() => {
    isEventLogOpen.value = true;
});

const handleFileSaved = () => {
    isEditFileOpen.value = false;
    searchFiles();
};

const handleFileDeleted = () => {
    addToast({
        detail: `File reference # ${selectedFile.value?.ref_number} has been deleted`,
    });
    isDeleteFileOpen.value = false;
    selectedFile.value = undefined;
    searchFiles();
};

const handleRowClick = ({ data }: FileRowSlot) => {
    if (!data.routes?.details) {
        return;
    }

    safeRedirect(data.routes.details);
};
</script>

<style scoped lang="scss">
:deep(.money-cell) {
    // overrides DsColumn's left alignment for header. Cleanup when CLENG-3774 ships a prop to DsColumn to set alignment
    [data-pc-section='headercontent'] {
        justify-content: flex-end !important;
    }
}
</style>
